import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { CircleHelp } from "lucide-react";

type Props = {
  children: ReactNode;
};

type PopoverPosition = {
  left: number;
  top: number;
  width: number;
};

const VIEWPORT_GUTTER = 12;
const POPOVER_WIDTH = 288;
const POPOVER_OFFSET = 8;

/** Small, keyboard-accessible help popover for compact form labels. */
export function FieldHelp({ children }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const rootRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLSpanElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const popoverId = useId();

  function cancelClose(): void {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  function scheduleClose(): void {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 100);
  }

  useEffect(() => {
    return () => cancelClose();
  }, []);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    let frameId: number | undefined;
    const updatePosition = (): void => {
      const anchor = buttonRef.current?.getBoundingClientRect();
      if (!anchor) return;

      const width = Math.min(POPOVER_WIDTH, window.innerWidth - VIEWPORT_GUTTER * 2);
      const left = Math.min(
        Math.max(VIEWPORT_GUTTER, anchor.left),
        window.innerWidth - width - VIEWPORT_GUTTER,
      );
      const popoverHeight = popoverRef.current?.offsetHeight ?? 0;
      const spaceBelow = window.innerHeight - anchor.bottom;
      const top =
        popoverHeight > 0 &&
        spaceBelow < popoverHeight + POPOVER_OFFSET &&
        anchor.top >= popoverHeight + POPOVER_OFFSET + VIEWPORT_GUTTER
          ? anchor.top - popoverHeight - POPOVER_OFFSET
          : anchor.bottom + POPOVER_OFFSET;

      setPosition({ left, top, width });
    };

    updatePosition();
    frameId = window.requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      if (frameId !== undefined) window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  const popover =
    open && position && typeof document !== "undefined"
      ? createPortal(
          <span
            ref={popoverRef}
            id={popoverId}
            role="tooltip"
            style={{ left: position.left, top: position.top, width: position.width }}
            className="fixed z-[100] rounded-md border border-border bg-bg px-3 py-2 text-left text-xs normal-case tracking-normal text-fg shadow-xl break-words"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            {children}
          </span>,
          document.body,
        )
      : null;

  return (
    <span
      ref={rootRef}
      className="inline-flex align-middle"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          cancelClose();
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted transition-colors hover:text-fg focus:outline-none focus-visible:ring-1 focus-visible:ring-fg"
        aria-label="About this setting"
        aria-expanded={open}
        aria-describedby={open ? popoverId : undefined}
      >
        <CircleHelp size={14} strokeWidth={1.5} aria-hidden="true" />
      </button>
      {popover}
    </span>
  );
}
